import React, { useState, useEffect } from 'react';
import { ExternalLink, X, Copy, Check } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

function isInAppBrowser() {
  // Never show inside the native Capacitor app — it's not a social in-app browser
  if (Capacitor.isNativePlatform()) return false;
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FBIOS|FB_IAB|MessengerForiOS|Messenger\/|Instagram|LinkedInApp|Twitter\/|Snapchat|TikTok|BytedanceWebview/i.test(ua);
}

function isFacebookMessenger() {
  const ua = navigator.userAgent || '';
  return /FBAN\/Messenger|MessengerForiOS|Messenger\//i.test(ua);
}

function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

export default function InAppBrowserBanner() {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isInAppBrowser()) setVisible(true);
  }, []);

  if (!visible) return null;

  const currentUrl = window.location.href;
  const isMessenger = isFacebookMessenger();
  const android = isAndroid();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard API blocked in some in-app browsers
    }
  };

  // Android: intent URL launches Chrome directly
  const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center sm:items-center pointer-events-none">
      <div
        className="pointer-events-auto w-full sm:max-w-md mx-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 text-base">
              Open in your browser
            </span>
          </div>
          <button
            onClick={() => setVisible(false)}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-2">
          <p className="text-sm text-gray-600 leading-relaxed">
            {isMessenger
              ? 'This page is open inside Messenger. Google Sign-In requires your browser — please open it externally.'
              : 'This page is open inside an in-app browser. Google Sign-In requires your browser — please open it externally.'}
          </p>
        </div>

        {/* iOS instructions — Safari cannot be force-opened from Messenger's WebView */}
        {!android && (
          <div className="mx-5 mb-3 bg-blue-50 rounded-xl px-4 py-3">
            <p className="text-sm text-blue-800">
              Tap <strong>···</strong> (bottom right) → <strong>Open in Safari</strong>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 px-5 pt-1 pb-5">
          {android && (
            // Android: intent:// opens Chrome directly — use <a> so the browser handles it natively
            <a
              href={intentUrl}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-3 px-4 rounded-xl transition-colors no-underline"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Chrome
            </a>
          )}

          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium py-3 px-4 rounded-xl transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      </div>
    </div>
  );
}
